import { useInvestmentAccountDetail, useInvestmentInstrumentDetail } from '@egw/api'
import { GenericError, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'
import { extractAllocationHoldingsDetails } from '../../utils'

export const AllocationHoldingsDetails: FC = () => {
  const { t } = useTranslation('features-holdings')

  const {
    alias = '',
    assetAllocation = '',
    symbol = '',
  } = useParams<{
    alias: string
    assetAllocation: string
    symbol: string
  }>()

  const {
    data: investmentAccountDetail,
    isLoading: holdingLoading,
    isError: holdingDetailError,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: Boolean(alias),
  })

  const holdingDetail = extractAllocationHoldingsDetails({
    data: investmentAccountDetail,
    assetAllocationParam: assetAllocation,
    symbolParam: symbol,
  })

  const {
    data: investmetInstrument,
    isLoading: instrumentLoading,
    isError: investmetInstrumentError,
  } = useInvestmentInstrumentDetail(symbol)

  const pageSubtitle = investmentAccountDetail?.accountName

  const pageTitle = t('holding_details.title')

  const holdingAttributes = holdingDetail
    ? [
        {
          label: t('holding_details.labels.holding_name'),
          value: holdingDetail.name,
        },
        {
          label: t('holding_details.labels.previous_close'),
          value: investmetInstrument?.priorClosePrice?.amount
            ? buildBasicCurrency({
                amount: investmetInstrument.priorClosePrice.amount,
                currencyCode: investmetInstrument.priorClosePrice.currencyCode,
              })
            : t('holding_details.empty_value'),
        },
        {
          label: t('holding_details.labels.quantity_owned'),
          value: holdingDetail.quantity
            ? t('holding_details.shares', { quantity: Number(holdingDetail.quantity).toFixed(2) })
            : t('holding_details.empty_value'),
        },
        {
          label: t(`holding_details.labels.amount_owned`),
          value: buildBasicCurrency({
            amount: holdingDetail.amount.amount,
            currencyCode: holdingDetail.amount.currencyCode,
          }),
        },
        // TODO: UNCOMMENT ONCE APEX INTRODUCES FIELD
        // ENSURE FIELD IS BEING QUERIED FROM API
        // {
        //   label: t('holding_details.labels.cost_basis'),
        //   value: buildBasicCurrency({
        //     amount: holdingDetail.costBasis?.amount ?? 0,
        //     currencyCode: holdingDetail.costBasis?.currencyCode ?? 'USD',
        //   }),
        // },
      ]
    : []

  return (
    <>
      {holdingDetailError ? (
        <div className="egw-container">
          <GenericError errorMessage={t('holdings.overview_fatal_error')} />
        </div>
      ) : null}
      {holdingLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!holdingLoading && investmentAccountDetail ? (
        <>
          <PageHeader
            title={pageTitle}
            subtitle={pageSubtitle}
            backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.backPath
              ?.replace(':alias', alias.toString())
              .replace(':assetAllocation', assetAllocation)}
          />
          <div className="egw-container pt-0">
            {instrumentLoading && holdingLoading ? (
              <div className="w-full">
                <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                  <Skeleton count={1} height="100px" className="mb-10" />
                  <Skeleton count={5} height="50px" />
                </SkeletonTheme>
              </div>
            ) : null}
            {investmetInstrumentError || holdingDetailError ? (
              <div className="default-page-width my-20">
                <GenericError errorMessage={t('holdings.overview_fatal_error')} />
              </div>
            ) : null}
            {holdingDetail ? (
              <div className="flex flex-col">
                <div className="typo-primary-2xl-light flex flex-row items-center mb-lg">{holdingDetail.symbol}</div>
                <div className="flex flex-col">
                  {holdingAttributes.map(({ label, value }: { label: string; value: string }) => (
                    <div
                      key={crypto.randomUUID()}
                      className="border-light flex flex-row justify-between border-b py-sm last-of-type:border-none"
                    >
                      <span className="typo-primary-base-light text-text-meta">{label}</span>
                      <span className="typo-primary-base-semibold text-text-header">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  )
}
